<script setup lang="ts">
import { URL_IMAGE } from '@/config/constants'
import { checkTruncationHappy } from '@/happyTheme/utils/product/checkTrunctationHappy'
import type { Product } from '@/storage/products'
import { convertPriceToEuro } from '@/utils/price/convertPrice'
import { getProductName } from '@/utils/product/getProductName'
import { nextTick, onMounted, ref } from 'vue'

const props = defineProps<{
    product: Product
    description: string
}>()

const maxLines = ref<number>(1)
const descriptionElement = ref<HTMLElement>()
const divDescriptionElement = ref<HTMLElement>()
const buttonElementSelect = ref<HTMLElement>()

onMounted(() => {
    nextTick(() => {
        checkTruncationHappy(
            descriptionElement,
            divDescriptionElement,
            buttonElementSelect,
            maxLines
        )
        window.addEventListener('resize', () =>
            checkTruncationHappy(
                descriptionElement,
                divDescriptionElement,
                buttonElementSelect,
                maxLines
            )
        )
    })
})
</script>

<template>
    <section class="section-parent-card-empty-product-happy">
        <section class="section-card-product-empty-happy">
            <div class="div-card-product-empty-happy">
                <img
                    class="img-card-product-empty-happy"
                    :src="`${URL_IMAGE}${props.product.image}`"
                    :alt="getProductName(props.product)"
                />
            </div>

            <div
                class="div-description-card-product-empty-happy"
                ref="divDescriptionElement"
            >
                <div class="div-description-parent-card-product-empty-happy">
                    <div
                        ref="descriptionElement"
                        class="div-description-child-card-product-empty-happy"
                    >
                        <h3 class="text-product-name-empty-happy">
                            {{ getProductName(props.product) }}
                        </h3>
                        <p class="text-price-product-empty-happy">
                            {{ convertPriceToEuro(props.product.price) + ' €' }}
                        </p>
                    </div>
                    <p
                        v-if="props.description !== 'Aucune description' && maxLines > 0"
                        :class="`line-clamp-${maxLines}`"
                        :style="{ '-webkit-line-clamp': maxLines }"
                        class="text-description-product-empty-happy"
                    >
                        {{ props.description }}
                    </p>
                </div>

                <div
                    class="div-button-select-product-empty-happy"
                    ref="buttonElementSelect"
                >
                    Sélectionner
                </div>
            </div>
        </section>
    </section>
</template>

<style scoped>
.section-parent-card-empty-product-happy {
    @apply w-full h-full flex justify-center items-center p-0.5;
}

.section-card-product-empty-happy {
    @apply flex flex-col justify-start items-center min-h-52 w-full h-full rounded-lg;
    @apply shadow happy-light:shadow-text-dark-happy-light happy-dark:shadow-bg-happy-light;
}

.section-card-product-empty-happy:active {
    @apply bg-border-red-happy-light;
}

.div-card-product-empty-happy {
    @apply w-full flex justify-center items-center;
}

.img-card-product-empty-happy {
    @apply h-28 w-full object-contain;
}

.div-description-card-product-empty-happy {
    @apply w-full h-full flex flex-col justify-between items-start p-2;
}

.div-description-parent-card-product-empty-happy {
    @apply w-full flex flex-col justify-start;
}

.div-description-child-card-product-empty-happy {
    @apply w-full flex justify-between items-start;
    @apply flex-shrink-0 happy-dark:text-bg-happy-light happy-light:text-text-dark-happy-light;
}
.text-product-name-empty-happy {
    @apply text-sm font-semibold text-wrap max-w-40;
}

.text-price-product-empty-happy {
    @apply text-sm font-bold text-nowrap text-button-yellow-happy-light pl-2;
}

.text-description-product-empty-happy {
    @apply text-xs happy-dark:text-bg-happy-light happy-light:text-text-dark-happy-light text-ellipsis overflow-hidden mt-1;
    display: -webkit-box;
    -webkit-box-orient: vertical;
}

.div-button-select-product-empty-happy {
    @apply w-full h-8 bg-button-yellow-happy-light text-center text-sm font-bold text-slate-eronor flex justify-center items-center rounded-md mt-2 cursor-pointer shadow shadow-text-dark-happy-light;
}

.section-card-product-empty-happy:active
    .div-description-card-product-empty-happy
    .div-description-child-card-product-empty-happy
    .text-product-name-empty-happy,
.section-card-product-empty-happy:active
    .div-description-card-product-empty-happy
    .text-description-product-empty-happy {
    @apply text-bg-happy-light;
}

@media (hover: hover) {
    .section-card-product-empty-happy:hover {
        @apply bg-border-red-happy-light;
    }

    .section-card-product-empty-happy:hover
        .div-description-card-product-empty-happy
        .div-description-child-card-product-empty-happy
        .text-product-name-empty-happy,
    .section-card-product-empty-happy:hover
        .div-description-card-product-empty-happy
        .text-description-product-empty-happy {
        @apply text-bg-happy-light;
    }
}
</style>
